import React, { useState, useEffect } from 'react';
import { articlesApi } from '../lib/api';
import { PageSEO } from '../components/seo/SchemaMarkup';
import { SectionNav } from './hyrox/SectionNav';
import { GUIDE_SLUGS, NAV_SECTIONS } from './hyrox/data';
import { HeroSection } from './hyrox/sections/HeroSection';
import { RaceSection } from './hyrox/sections/RaceSection';
import { VideosSection } from './hyrox/sections/VideosSection';
import { StationsSection, RacePlanSection } from './hyrox/sections/StationsAndRacePlan';
import { ToolsSection } from './hyrox/sections/ToolsSection';
import { GuidesSection } from './hyrox/sections/GuidesSection';
import { GymsSection } from './hyrox/sections/GymsSection';
import { EquipmentSection } from './hyrox/sections/EquipmentSection';
import { AffiliateSection } from './hyrox/sections/AffiliateSection';
import { FAQSection, NewsletterSection } from './hyrox/sections/FAQAndNewsletter';
import { Link } from 'react-router-dom';
import { BarChart3, ArrowRight } from 'lucide-react';

const BenchmarkReportBanner = () => (
  <section className="max-w-[1100px] mx-auto px-6 my-4" data-testid="benchmark-report-banner">
    <Link to="/hyrox/benchmark-report" className="group flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-[#111] border border-[#e8ff00]/30 hover:border-[#e8ff00] p-6 transition-colors">
      <div className="flex items-start gap-4">
        <BarChart3 className="w-8 h-8 text-[#e8ff00] shrink-0" />
        <div>
          <p className="font-mono text-[10px] tracking-[0.2em] text-[#e8ff00] uppercase mb-1">Original Data</p>
          <h3 className="font-heading text-2xl text-white leading-tight">The HYROX Benchmark Report</h3>
          <p className="text-zinc-500 text-sm mt-1">Average finish times & station splits by division — see exactly where you rank.</p>
        </div>
      </div>
      <span className="inline-flex items-center gap-2 text-[#e8ff00] font-mono text-xs tracking-wider uppercase group-hover:gap-3 transition-all shrink-0">
        View Report <ArrowRight className="w-4 h-4" />
      </span>
    </Link>
  </section>
);

export default function HyroxPage() {
  const [articles, setArticles] = useState([]);
  const [activeSection, setActiveSection] = useState('race');
  const [mapSelectedState, setMapSelectedState] = useState('');

  useEffect(() => {
    articlesApi.getAll({ category: 'HYROX', limit: 30 })
      .then((res) => setArticles(res.data || []))
      .catch((e) => console.error(e));
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = NAV_SECTIONS.map((s) => ({ id: s.id, el: document.getElementById(s.id) }));
      for (const s of sections.reverse()) {
        if (s.el && s.el.getBoundingClientRect().top <= 150) {
          setActiveSection(s.id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const guideArticles = articles.filter((a) => GUIDE_SLUGS.includes(a.slug));
  const otherGuides = articles.filter((a) => !GUIDE_SLUGS.includes(a.slug) && !a.slug.includes('best-hyrox-training-gyms'));

  return (
    <main className="pt-20 min-h-screen" data-testid="hyrox-page">
      <PageSEO
        title="HYROX Training Hub — Race Prep, Gym Finder & Equipment | NinjAthlete"
        description="Complete HYROX guide: training plans, workout strategies, gym finder, affiliate costs, race equipment, and city-by-city directories. Get race-ready."
        path="/hyrox"
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'HYROX Hub', url: '/hyrox' }]}
      />
      <HeroSection />
      <SectionNav activeSection={activeSection} />
      <RaceSection />
      <VideosSection />
      <StationsSection />
      <BenchmarkReportBanner />
      <RacePlanSection />
      <ToolsSection />
      <GuidesSection guideArticles={guideArticles} otherGuides={otherGuides} />
      <GymsSection mapSelectedState={mapSelectedState} setMapSelectedState={setMapSelectedState} />
      <EquipmentSection />
      <AffiliateSection />
      <FAQSection />
      <NewsletterSection />
    </main>
  );
}
